import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'jalali-moment';

import { LocalStorage } from '../../../services';
import { _get, _post, _SERVER_URL_ } from '../../../utils/network';
import { commafy, typeChecker } from '../../../utils/methods';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const CreditsList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('credits'), link: '/credits-list' },
		{ title: t('list') }
	];

	const [users, setUsers] = useState([]);
	let usersListData = {
		head: [
			t('fullname'),
			`${t('online')} (${t('permanent')}-${t('daily')})`,
			t('online_total'),
			`${t('station')} (${t('permanent')}-${t('daily')})`,
			t('station_total'),
			t('adjusted_remain'),
			t('status'),
			t('description'),
			t('status'),
			t('operation')
		],
		body: users.map(user => [
			user.user_fullname,
			<>
				{commafy(user.user_all_time_credit || '_') || '_'}
				<br />
				{commafy(user.user_daily_credit || '_') || '_'}
			</>,
			commafy(user.user_credit || '_') || '_',
			<>
				{commafy(user.user_all_time_credit_contract_value || '_') || '_'}
				<br />
				{commafy(user.user_daily_credit_contract_value || '_') || '_'}
			</>,
			commafy(user.user_credit_contract_value || '_') || '_',
			<span className={user.adjusted_remain < 0 ? "text-danger" : "text-success"}>{commafy(user.adjusted_remain)}</span>,
			user.has_conflict ? <span className="text-danger">{t('error_in_calculations')}</span> : '',
			user.credit_description,
			user.contract_status,
			<Link className='btn btn-secondary btn-sm p-1' to={`/users-edit/${user.user_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['rtl', 'ltr', 'ltr', 'ltr', 'ltr', 'ltr', '', 'rtl', 'rtl', 'ltr']
	};
	
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchUserFullname, setSearchUserFullname] = useState(null);
	const [searchUserMobile, setSearchUserMobile] = useState(null);
	const [searchUserNationalID, setSearchUserNationalID] = useState(null);
	const [searchUserTradeCode, setSearchUserTradeCode] = useState(null);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getUsers = async () => {
		let body = {
			user_fullname: searchUserFullname,
			phone_number: searchUserMobile,
			national_code: searchUserNationalID,
			trade_code: searchUserTradeCode,
			inviter_code: null,
			is_credit: 1,
			is_active: null,
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/users/admin/getAll', body);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.all_users);
				setUsers(response.data.users);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const filterUsers = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		let fullname = event.target.searchUserFullname.value.trim() || null;
		let mobile = event.target.searchUserMobile.value.trim() || null;
		let nationalID = event.target.searchUserNationalID.value.trim() || null;
		let tradeCode = event.target.searchUserTradeCode.value.trim() || null;
		setSearchUserFullname(fullname);
		setSearchUserMobile(mobile);
		setSearchUserNationalID(nationalID);
		setSearchUserTradeCode(tradeCode);
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};
	
	const getCreditsExcel = async event => {
		event.preventDefault();
		let startDate = event.target.creditsExcelStartDate.value.trim() || '';
		if(!!startDate && !typeChecker(startDate, 'shamsi-date'))
			return toast.warn(t('enter_start_date_correctly'));
		startDate = startDate ? moment(startDate, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD') : '';
		let endDate = event.target.creditsExcelEndDate.value.trim() || '';
		if(!!endDate && !typeChecker(endDate, 'shamsi-date'))
			return toast.warn(t('enter_end_date_correctly'));
		endDate = endDate ? moment(endDate, 'jYYYY/jMM/jDD').locale('en').format('YYYY-MM-DD') : '';
		let token = LocalStorage.get('token');
		let getCreditsExcelLink = `${_SERVER_URL_}/download-excel-user?start_date=${startDate}&end_date=${endDate}&token=${token}`;
		window.open(getCreditsExcelLink, "_blank", "noreferrer");
	};
	
	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getUsers();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('credits')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterUsers}>
						<div className="row gx-2">
							<FormField fieldKey="searchUserFullname" title={t('fullname')} value={searchUserFullname} isSearch={true} rtl />
							<FormField fieldKey="searchUserMobile" title={t('mobile')} value={searchUserMobile} isSearch={true} ltr />
							<FormField fieldKey="searchUserNationalID" title={t('national_id')} value={searchUserNationalID} isSearch={true} ltr />
							<FormField fieldKey="searchUserTradeCode" title={t('trade_code')} value={searchUserTradeCode} isSearch={true} ltr />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('credits')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={usersListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('get_excel')} {t('credits')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={getCreditsExcel}>
						<div className="row gx-2">
							<FormField fieldKey="creditsExcelStartDate" title={t('start_date')} isSearch={true} tip='1401/12/01' ltr />
							<FormField fieldKey="creditsExcelEndDate" title={t('end_date')} isSearch={true} tip='1401/12/01' ltr />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create">
								<i className="fa fa-download"></i> {t('get_excel')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default CreditsList;
